/**
 * Gifts core: pure helpers for the Gifts page finder (saved answers, vibe options, picks, close matches
 * and the reasons shown on each card). No DOM access, so the same file runs in the browser (as
 * Lumi.giftsCore) and in Node for tests/gifts-core.spec.js. The DOM turns reason() into words from the locale.
 */
(function (factory) {
  const core = factory();
  if (typeof module === 'object' && module.exports) {
    module.exports = core;
  } else {
    const Lumi = (window.Lumi = window.Lumi || {});
    Lumi.giftsCore = Lumi.giftsCore || core;
  }
})(function () {
  const ANY = 'any';
  const MAX_PICKS = 8;
  const MAX_CLOSE = 4;
  const OVERSPEND = 1.2;

  function valid(value, list) {
    return typeof value === 'string' && (value === ANY || list.indexOf(value) !== -1);
  }

  // The saved answers, or null when anything is missing or not one of the allowed values.
  function parseState(raw, options) {
    if (typeof raw !== 'string' || !raw) return null;
    let data;
    try {
      data = JSON.parse(raw);
    } catch (e) {
      return null;
    }
    if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
    if (!valid(data.for, options.recipients) || !valid(data.vibe, options.vibes) || !valid(data.budget, options.budgets)) return null;
    return { for: data.for, vibe: data.vibe, budget: data.budget };
  }

  // Answers passed in the URL (?for=&vibe=&budget=). Unknown values are dropped.
  function fromParams(search, options) {
    const params = new URLSearchParams(search || '');
    const lists = { for: options.recipients, vibe: options.vibes, budget: options.budgets };
    const state = {};
    Object.keys(lists).forEach((key) => {
      const value = params.get(key);
      if (valid(value, lists[key])) state[key] = value;
    });
    return state;
  }

  // Sold out and untagged items never match; otherwise the item must be tagged for the recipient.
  function isCandidate(item, recipient) {
    if (!item.available || !item.for.length) return false;
    return recipient === ANY || item.for.indexOf(recipient) !== -1;
  }

  function carriesVibe(item, vibe) {
    return vibe === ANY || item.vibes.indexOf(vibe) !== -1;
  }

  function withinBudget(item, maxCents) {
    return maxCents === null || item.priceCents <= maxCents;
  }

  function byPrice(a, b) {
    return a.priceCents - b.priceCents;
  }

  // The vibe keys to offer for a recipient, in block order, skipping vibes no available product carries.
  function vibesFor(recipient, vibeBlocks, items) {
    const pool = items.filter((item) => isCandidate(item, recipient));
    return vibeBlocks
      .filter((block) => recipient === ANY || block.for.indexOf(recipient) !== -1)
      .filter((block) => pool.some((item) => item.vibes.indexOf(block.key) !== -1))
      .map((block) => block.key);
  }

  // Picks: the vibe and budget both fit, cheapest first. Close matches: the vibe fits but the price is up to
  // 20% over budget (cheapest first), then in-budget items with another vibe (cheapest first).
  function match(items, answers) {
    const pool = items.filter((item) => isCandidate(item, answers.for));
    const picks = pool.filter((item) => carriesVibe(item, answers.vibe) && withinBudget(item, answers.maxCents)).sort(byPrice);
    const limit = answers.maxCents === null ? null : Math.floor(answers.maxCents * OVERSPEND);
    const overspend = pool.filter((item) => carriesVibe(item, answers.vibe) && !withinBudget(item, answers.maxCents) && item.priceCents <= limit).sort(byPrice);
    const otherVibe = pool.filter((item) => !carriesVibe(item, answers.vibe) && withinBudget(item, answers.maxCents)).sort(byPrice);
    return { picks: picks.slice(0, MAX_PICKS), close: overspend.concat(otherVibe).slice(0, MAX_CLOSE) };
  }

  // Why an item was chosen: the vibe it carries (null when the shopper had no vibe in mind) and how it sits against the budget.
  function reason(item, answers) {
    let vibe = null;
    if (answers.vibe !== ANY) vibe = item.vibes.indexOf(answers.vibe) !== -1 ? answers.vibe : item.vibes[0] || null;
    let budget = null;
    if (answers.maxCents !== null) budget = item.priceCents <= answers.maxCents ? 'under' : 'over';
    return { vibe, budget };
  }

  return { parseState, fromParams, vibesFor, match, reason };
});
